import { useRouter, useSearchParams } from "next/navigation";
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import Loaders from "@stadium/ui/src/components/loaders/loaders";
import { useAuth0V2 } from "@stadium/services/auth0";

import {
  getDefaultPreviewModeCurrency,
  getStorePreviewModeCartKey,
  getStorePreviewModeCurrencyKey,
  getStorePreviewModeKey,
} from "@helpers/store";
import { SESSION_STORAGE, useWindowStorage } from "@hooks/use-window-storage";

import { getCartOrder } from "@selectors/cart";
import { resetCart, updateCart } from "@slices/cart";
import { setStoreBalance, updateStoreConfig } from "@slices/store";
import { convertPrice } from "@helpers/currency";

import { PREVIEW_MODE_DEFAULT_STORE_BALANCE } from "@constants/preview-mode";
import { getStoreBasePath } from "@constants/routes";
import {
  getIsLockerShop,
  getStoreCacheKey,
  getStoreCurrencyCode,
  getStoreIsOrderFetching,
} from "@selectors/store";

import { getConversionRates } from "@stadium/redux-utils/selectors/configSelector";
import { useConfig } from "@stadium/hooks/lib/use-config";

import { useCreateShopContext } from "@contexts/create-shop-context";
import { useSendItemsContext } from "@contexts/send-items-context";
import { useStoreContext } from "@components/store/provider";

import { updateLocalCartCurrency } from "@helpers/orders/cart";
import { POINTS_CURRENCY_CODE } from "@constants/store";
import { PRICELESS_CURRENCY_OBJ } from "@stadium/constants/currency";

const DEFAULT_OBJ = {};

export const PreviewModeContext = React.createContext();

export const usePreviewModeContext = () => useContext(PreviewModeContext) || DEFAULT_OBJ;

const PreviewModeProvider = ({ children, storeNumber }) => {
  const { isAuthenticated } = useAuth0V2();
  const dispatch = useDispatch();
  const router = useRouter();
  const searchParam = useSearchParams();
  const preview = searchParam.get("preview");
  const isCreateShopFlow = searchParam.get("createShop");

  const [previewMode, setPreviewMode, deletePreviewMode] = useWindowStorage(
    getStorePreviewModeKey(storeNumber),
    false,
    SESSION_STORAGE,
  );
  const [redirecting, setRedirecting] = useState(false);

  const { countryCode, lang, storeCode, isNewRoute, workspaceSlug } = useStoreContext();

  const { clearDummyStoreParams } = useCreateShopContext();

  const { isSendToMyself } = useSendItemsContext();

  const isStoreOrderFetching = useSelector(getStoreIsOrderFetching);
  const storeCurrencyCode = useSelector(getStoreCurrencyCode);
  const conversionRates = useConfig(getConversionRates);
  const isLockerShop = useSelector(getIsLockerShop);
  const storeCacheKey = useSelector(getStoreCacheKey);

  const cartOrder = useSelector(getCartOrder);
  const isGuestOrPreviewMode = !isAuthenticated || previewMode;
  const [previewModeCartOrder, setPreviewModeCartOrder, clearPreviewModeCart] = useWindowStorage(
    getStorePreviewModeCartKey(storeNumber, countryCode, isSendToMyself),
    isGuestOrPreviewMode ? cartOrder : undefined,
  );

  const [previewModeCurrency, setPreviewModeCurrency, deletePreviewModeCurrency] = useWindowStorage(
    getStorePreviewModeCurrencyKey(storeNumber),
    "",
  );

  //ToDo: This is temporary fix for priceless currency code, Remove this after 1 week.
  useEffect(() => {
    if (previewModeCurrency?.currencyCode === PRICELESS_CURRENCY_OBJ.code) {
      setPreviewModeCurrency("");
    }
  }, [previewModeCurrency]);

  const storeBasePath = getStoreBasePath({
    countryCode,
    lang,
    storeCode,
    storeNumber,
    isNewRoute,
    workspaceSlug,
  });

  useEffect(() => {
    if (preview && !previewMode && !isLockerShop) {
      router.replace({ pathname: storeBasePath, query: {} });
      setPreviewMode(preview === "true");
    }
  }, [router, setPreviewMode, preview, previewMode, storeBasePath, isLockerShop]);

  useEffect(() => {
    if (previewMode && storeNumber && previewModeCurrency) {
      const previewStoreBalance = convertPrice({
        from: POINTS_CURRENCY_CODE,
        to: previewModeCurrency.currencyCode,
        value: PREVIEW_MODE_DEFAULT_STORE_BALANCE,
        conversionRates,
      });

      dispatch(setStoreBalance({ balance: previewStoreBalance }));
    }
  }, [previewMode, storeNumber, previewModeCartOrder, previewModeCurrency, conversionRates]);

  useEffect(() => {
    if (isCreateShopFlow) {
      deletePreviewMode();
    }
  }, [isCreateShopFlow]);

  //update local cart from local storage in preview mode and guest mode
  useEffect(() => {
    if (isGuestOrPreviewMode && storeNumber && previewModeCartOrder) {
      dispatch(updateCart(previewModeCartOrder));
    }
  }, [previewMode, storeNumber, previewModeCartOrder]);

  useEffect(() => {
    if (previewMode && cartOrder?.id && storeCurrencyCode) {
      const updatedCart = updateLocalCartCurrency(
        { ...cartOrder, currency: previewModeCurrency.currencyCode },
        previewModeCurrency.currencyCode,
        conversionRates,
      );
      setPreviewModeCartOrder(updatedCart);
    }
  }, [previewModeCurrency]);

  useEffect(() => {
    if (!previewModeCurrency && storeCurrencyCode) {
      setPreviewModeCurrency(getDefaultPreviewModeCurrency(storeCurrencyCode));
    }
  }, [storeCurrencyCode]);

  useEffect(() => {
    if (previewMode && previewModeCurrency && previewModeCurrency !== storeCurrencyCode) {
      dispatch(updateStoreConfig(previewModeCurrency));
    }
  }, [previewModeCurrency, previewMode, isStoreOrderFetching, storeNumber, storeCurrencyCode]);

  useEffect(() => {
    if (isLockerShop && preview) {
      resetPreviewMode(storeBasePath);
    }
  }, [isLockerShop, storeBasePath, preview]);

  useEffect(() => {
    if (previewMode) clearDummyStoreParams();
  }, [previewMode]);

  const getStoreUpdatedAt = useCallback(() => {
    const currentTime = new Date().getTime();
    return previewMode ? currentTime.toString() : storeCacheKey;
  }, [storeCacheKey, previewMode]);

  const resetPreviewMode = useCallback(
    (path) => {
      setRedirecting(true);
      setPreviewMode(false);
      clearPreviewModeCart();
      deletePreviewMode();
      deletePreviewModeCurrency();
      dispatch(resetCart());
      window.location.href = path;
    },
    [setPreviewMode, clearPreviewModeCart],
  );

  const value = useMemo(
    () => ({
      previewMode: previewMode || preview === "true",
      isGuestOrPreviewMode,
      setPreviewMode,
      resetPreviewMode,
      setPreviewModeCurrency,
      previewModeCurrency,
      getStoreUpdatedAt,
    }),
    [
      preview,
      previewMode,
      isGuestOrPreviewMode,
      resetPreviewMode,
      setPreviewMode,
      setPreviewModeCurrency,
      previewModeCurrency,
      getStoreUpdatedAt,
    ],
  );

  return (
    <PreviewModeContext.Provider value={value}>
      {redirecting ? <Loaders.PageLoader wrapperClass="pageLoaderwrap" /> : <>{children}</>}
    </PreviewModeContext.Provider>
  );
};

export default PreviewModeProvider;
